// rollupDefinitions.js

// Utilities
import { generateKey } from 'utilities/Keys';
import { normalizeSchemaFieldType } from 'utilities/FieldTypes';

export const ROLLUP_VALUE_COLOR_AUTO = 'auto';

const ROLLUP_FIELD_FAMILY_BY_TYPE = {
  annualdate: 'temporal_point',
  barcode: 'text',
  boolean: 'boolean',
  checklist: 'checklist',
  money: 'numeric',
  date: 'temporal_point',
  daterange: 'temporal_range',
  datetime: 'temporal_point',
  datetimerange: 'temporal_range',
  email: 'text',
  enum: 'categorical_single',
  measurement: 'measurement',
  multiselect: 'categorical_multi',
  relation: 'categorical_multi',
  number: 'numeric',
  people: 'categorical_multi',
  percentage: 'numeric',
  phone: 'text',
  richtext: 'text',
  signature: 'text',
  string: 'text',
  url: 'text',
};

const ROLLUP_AGGREGATE_DEFINITIONS = {
  active_now_count: {
    families: ['temporal_range'],
    label: 'Active now',
  },
  all_true: {
    families: ['boolean'],
    label: 'All true',
  },
  any_true: {
    families: ['boolean'],
    label: 'Any true',
  },
  average: {
    families: ['numeric', 'measurement'],
    label: 'Average',
  },
  average_completion_percent: {
    families: ['checklist'],
    label: 'Average completion %',
  },
  count_false: {
    families: ['boolean'],
    label: 'Count false',
  },
  count_fully_completed: {
    families: ['checklist'],
    label: 'Completed checklists',
  },
  count_non_empty: {
    families: ['numeric', 'measurement', 'temporal_point', 'temporal_range', 'categorical_single', 'categorical_multi', 'text'],
    label: 'Count filled',
  },
  count_not_started: {
    families: ['checklist'],
    label: 'Not started checklists',
  },
  count_started: {
    families: ['checklist'],
    label: 'Started checklists',
  },
  count_true: {
    families: ['boolean'],
    label: 'Count true',
  },
  earliest: {
    families: ['temporal_point'],
    label: 'Earliest',
  },
  earliest_start: {
    families: ['temporal_range'],
    label: 'Earliest start',
  },
  latest: {
    families: ['temporal_point'],
    label: 'Latest',
  },
  latest_end: {
    families: ['temporal_range'],
    label: 'Latest end',
  },
  least_common: {
    families: ['categorical_single', 'categorical_multi'],
    label: 'Least common',
  },
  max: {
    families: ['numeric', 'measurement'],
    label: 'Maximum',
  },
  min: {
    families: ['numeric', 'measurement'],
    label: 'Minimum',
  },
  most_common: {
    families: ['categorical_single', 'categorical_multi'],
    label: 'Most common',
  },
  percent_false: {
    families: ['boolean'],
    label: 'False %',
  },
  percent_true: {
    families: ['boolean'],
    label: 'True %',
  },
  show_list: {
    families: ['categorical_single', 'categorical_multi', 'text'],
    label: 'Show list',
  },
  sum: {
    families: ['numeric', 'measurement'],
    label: 'Sum',
  },
  sum_completed_items: {
    families: ['checklist'],
    label: 'Completed items',
  },
  sum_total_items: {
    families: ['checklist'],
    label: 'Total checklist items',
  },
  unique_count: {
    families: ['categorical_single', 'categorical_multi', 'text'],
    label: 'Unique count',
  },
};

const DEFAULT_ROLLUP_AGGREGATE = 'count_non_empty';

const normalizeString = (value) => String(value || '').trim();

const normalizeBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  return fallback;
};

export const normalizeRollupValueColor = (value) => {
  const normalized = normalizeString(value).toLowerCase();
  if (!normalized || normalized === ROLLUP_VALUE_COLOR_AUTO) return ROLLUP_VALUE_COLOR_AUTO;
  if (/^#[0-9a-f]{6}$/i.test(normalized)) return normalized;
  return ROLLUP_VALUE_COLOR_AUTO;
};

export const getRollupFieldType = (fieldDef) => {
  return normalizeSchemaFieldType(fieldDef?.type);
};

export const getRollupFieldFamily = (fieldDef) => {
  const fieldType = getRollupFieldType(fieldDef);
  return ROLLUP_FIELD_FAMILY_BY_TYPE[fieldType] || null;
};

export const getRollupAggregateLabel = (aggregate) => {
  return ROLLUP_AGGREGATE_DEFINITIONS[normalizeString(aggregate).toLowerCase()]?.label || 'Aggregate';
};

export const getRollupFieldLabel = (fieldDef, fallback = 'Field') => {
  return normalizeString(fieldDef?.label || fieldDef?.title || fieldDef?.name || fieldDef?.key) || fallback;
};

export const getRollupAggregateOptions = (fieldDef) => {
  const family = getRollupFieldFamily(fieldDef);
  if (!family) return [];

  return Object.entries(ROLLUP_AGGREGATE_DEFINITIONS)
    .filter(([, definition]) => definition.families.includes(family))
    .map(([value, definition]) => ({
      label: definition.label,
      value,
    }));
};

export const buildRollupDefaultTitle = ({ aggregate, fieldDef } = {}) => {
  const aggregateLabel = getRollupAggregateLabel(aggregate);
  const fieldLabel = getRollupFieldLabel(fieldDef);
  return `${aggregateLabel} ${fieldLabel}`.trim();
};

export const normalizeRollupDefinition = (rollup, { typeKey, index = 0 } = {}) => {
  const key = normalizeString(rollup?.key) || generateKey();
  const fieldKey = normalizeString(rollup?.fieldKey);
  const aggregate = normalizeString(rollup?.aggregate).toLowerCase() || DEFAULT_ROLLUP_AGGREGATE;
  const fallbackFieldDef = fieldKey
    ? {
      key: fieldKey,
      name: fieldKey,
    }
    : null;
  const title = normalizeString(rollup?.title) || buildRollupDefaultTitle({
    aggregate,
    fieldDef: fallbackFieldDef,
  });

  return {
    key,
    title,
    description: normalizeString(rollup?.description),
    typeKey: normalizeString(rollup?.typeKey || typeKey) || null,
    fieldKey,
    aggregate,
    position: Number.isFinite(Number(rollup?.position)) ? Number(rollup.position) : index,
    pinToDatabaseSummary: normalizeBoolean(rollup?.pinToDatabaseSummary, false),
    pinToRelatedSummary: normalizeBoolean(rollup?.pinToRelatedSummary, false),
    valueColor: normalizeRollupValueColor(rollup?.valueColor),
  };
};

export const normalizeRollupDefinitions = (rollups, { typeKey } = {}) => {
  return (Array.isArray(rollups) ? rollups : [])
    .map((rollup, index) => normalizeRollupDefinition(rollup, { index, typeKey }))
    .filter((rollup) => Boolean(rollup?.fieldKey))
    .sort((left, right) => {
      const leftPosition = Number.isFinite(Number(left?.position)) ? Number(left.position) : 0;
      const rightPosition = Number.isFinite(Number(right?.position)) ? Number(right.position) : 0;
      return leftPosition - rightPosition;
    })
    .map((rollup, index) => ({
      ...rollup,
      position: index,
    }));
};

export const createDefaultRollupDraft = ({ fields = [], typeKey = null } = {}) => {
  const fieldList = Array.isArray(fields) ? fields : [];
  const firstField = fieldList.find((field) => getRollupAggregateOptions(field).length > 0) || null;
  const defaultAggregate = firstField
    ? (getRollupAggregateOptions(firstField)[0]?.value || DEFAULT_ROLLUP_AGGREGATE)
    : DEFAULT_ROLLUP_AGGREGATE;

  return normalizeRollupDefinition({
    aggregate: defaultAggregate,
    fieldKey: normalizeString(firstField?.name || firstField?.key),
    pinToDatabaseSummary: true,
    pinToRelatedSummary: true,
    title: buildRollupDefaultTitle({
      aggregate: defaultAggregate,
      fieldDef: firstField,
    }),
    typeKey,
    valueColor: ROLLUP_VALUE_COLOR_AUTO,
  }, { typeKey });
};

export const summarizeRollupDefinition = (rollup, fieldMap) => {
  const fieldDef = fieldMap?.get?.(rollup?.fieldKey) || null;
  const fieldLabel = getRollupFieldLabel(fieldDef, normalizeString(rollup?.fieldKey) || 'Field');
  const aggregateLabel = getRollupAggregateLabel(rollup?.aggregate);
  const summaryTargets = [
    rollup?.pinToDatabaseSummary ? 'Collection Summary' : '',
    rollup?.pinToRelatedSummary ? 'Related Summary' : '',
  ].filter(Boolean);

  const parts = [`${aggregateLabel} of ${fieldLabel}`];
  if (summaryTargets.length) parts.push(summaryTargets.join(' + '));
  return parts.join(' • ');
};
